import { invokeFunction, ApiError } from "@/lib/api";
import { isSupabaseConfigured } from "@/lib/env";
import { supabase } from "@/lib/supabase";
import type { AppUser } from "@/features/auth/types";
import type { LoadListMeta, LoadRecord, LoadStatus } from "./types";

export type { LoadListMeta, LoadRecord, LoadSource, LoadStatus } from "./types";
export { DRIVER_STATUS_OPTIONS } from "./types";

interface LoadRow {
  id: string;
  reference: string;
  origin: string;
  destination: string;
  status: LoadStatus;
  pickup_date: string | null;
  delivery_date: string | null;
  carrier_name: string | null;
  driver_name: string | null;
  driver_profile_id: string | null;
  carrier_profile_id: string | null;
  customer_ref: string | null;
  source: LoadRecord["source"];
  quickmanage_id: string | null;
  internal_notes: string | null;
  updated_at: string;
}

const LS_KEY = "bb_loads";
const LS_SEED_VERSION_KEY = "bb_loads_seed_version";
const SEED_VERSION = 1;

const seedLoads: LoadRecord[] = [
  {
    id: "load-10482",
    reference: "BB-10482",
    origin: "Aurora, IL",
    destination: "Columbus, OH",
    status: "in_transit",
    pickup: "2026-05-31",
    delivery: "2026-06-01",
    carrierName: "Bull and Bear Co",
    driverName: "Preview Driver",
    equipmentId: "QM-TRUCK-563",
    equipmentUnit: "563",
    driverProfileId: "profile-driver",
    carrierProfileId: null,
    customerRef: "QM-CUST-1001",
    source: "quickmanage",
    quickmanageId: "QM-10482",
    internalNotes: "",
    equipmentWarnings: [
      {
        severity: "danger",
        message: "Fleetio marks unit 563 as Inactive while QuickManage has it assigned to this load.",
        quickmanageUnit: "563",
        fleetioUnit: "563",
        quickmanageStatus: "active",
        fleetioStatus: "Inactive",
      },
    ],
    updatedAt: "2026-06-17T12:00:00.000Z",
  },
  {
    id: "load-10485",
    reference: "BB-10485",
    origin: "Joliet, IL",
    destination: "Indianapolis, IN",
    status: "dispatched",
    pickup: "2026-06-02",
    delivery: "2026-06-02",
    carrierName: "Bull and Bear Co",
    driverName: "Preview Driver",
    equipmentId: "QM-TRUCK-637",
    equipmentUnit: "637",
    driverProfileId: "profile-driver",
    carrierProfileId: null,
    customerRef: "QM-CUST-1001",
    source: "quickmanage",
    quickmanageId: "QM-10485",
    internalNotes: "",
    equipmentWarnings: [],
    updatedAt: "2026-06-17T11:30:00.000Z",
  },
  {
    id: "load-10490",
    reference: "BB-10490",
    origin: "Chicago, IL",
    destination: "St. Louis, MO",
    status: "booked",
    pickup: "2026-06-03",
    delivery: "2026-06-03",
    carrierName: "",
    driverName: "",
    equipmentId: "",
    equipmentUnit: "",
    driverProfileId: null,
    carrierProfileId: null,
    customerRef: "QM-CUST-1001",
    source: "spreadsheet",
    quickmanageId: "",
    internalNotes: "",
    equipmentWarnings: [],
    updatedAt: "2026-06-17T10:00:00.000Z",
  },
];

const supabaseReady = () => isSupabaseConfigured && !!supabase;

const byPickupDesc = (a: LoadRecord, b: LoadRecord) =>
  new Date(b.pickup || b.updatedAt).getTime() - new Date(a.pickup || a.updatedAt).getTime();

function rowToLoad(row: LoadRow): LoadRecord {
  return {
    id: row.id,
    reference: row.reference,
    origin: row.origin,
    destination: row.destination,
    status: row.status,
    pickup: row.pickup_date ?? "",
    delivery: row.delivery_date ?? "",
    carrierName: row.carrier_name ?? "",
    driverName: row.driver_name ?? "",
    equipmentId: "",
    equipmentUnit: "",
    driverProfileId: row.driver_profile_id,
    carrierProfileId: row.carrier_profile_id,
    customerRef: row.customer_ref ?? "",
    source: row.source,
    quickmanageId: row.quickmanage_id ?? "",
    internalNotes: row.internal_notes ?? "",
    equipmentWarnings: [],
    updatedAt: row.updated_at,
  };
}

function previewProfileId(userId: string): string {
  if (userId.startsWith("dev-")) {
    const role = userId.slice(4);
    if (role === "driver") return "profile-driver";
    if (role === "carrier") return "profile-carrier";
    if (role === "customer") return "profile-customer";
    if (role === "internal") return "profile-internal";
  }
  return userId;
}

function previewCustomerRef(userId: string): string {
  if (userId === "dev-customer" || userId === "profile-customer") {
    return "QM-CUST-1001";
  }
  return "";
}

function filterLoadsForViewer(loads: LoadRecord[], viewer: AppUser): LoadRecord[] {
  const profileId = previewProfileId(viewer.id);

  switch (viewer.role) {
    case "internal":
      return loads;
    case "driver":
      return loads.filter((load) => load.driverProfileId === profileId);
    case "carrier":
      return loads.filter(
        (load) =>
          load.carrierProfileId === profileId ||
          (!load.carrierProfileId && load.status === "booked"),
      );
    case "customer": {
      const customerRef = previewCustomerRef(viewer.id);
      return loads.filter((load) => load.customerRef === customerRef);
    }
    default:
      return [];
  }
}

function mergeSeedLoads(existing: LoadRecord[]): LoadRecord[] {
  const byId = new Map(existing.map((load) => [load.id, load]));
  for (const seed of seedLoads) {
    if (!byId.has(seed.id)) {
      byId.set(seed.id, seed);
    }
  }
  return [...byId.values()];
}

function localReadAll(): LoadRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    const version = Number(localStorage.getItem(LS_SEED_VERSION_KEY) ?? "0");
    if (raw) {
      const parsed = (JSON.parse(raw) as LoadRecord[]).map((load) => ({
        ...load,
        equipmentId: load.equipmentId ?? "",
        equipmentUnit: load.equipmentUnit ?? "",
        equipmentWarnings: load.equipmentWarnings ?? [],
      }));
      if (version < SEED_VERSION) {
        const merged = mergeSeedLoads(parsed);
        localStorage.setItem(LS_KEY, JSON.stringify(merged));
        localStorage.setItem(LS_SEED_VERSION_KEY, String(SEED_VERSION));
        return merged;
      }
      return parsed;
    }
  } catch {
    /* ignore malformed cache */
  }
  localStorage.setItem(LS_KEY, JSON.stringify(seedLoads));
  localStorage.setItem(LS_SEED_VERSION_KEY, String(SEED_VERSION));
  return [...seedLoads];
}

function localWriteAll(loads: LoadRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(loads));
}

async function listLoadsFromEdge(): Promise<{ loads: LoadRecord[]; meta: LoadListMeta }> {
  return invokeFunction<{ loads: LoadRecord[]; meta: LoadListMeta }>("loads", {
    action: "list",
  });
}

/** Interim fallback when the edge function is not deployed yet. */
async function listLoadsFromDbOverlay(viewer?: AppUser | null): Promise<LoadRecord[]> {
  const { data, error } = await supabase!
    .from("loads")
    .select("*")
    .order("pickup_date", { ascending: false });
  if (error) throw error;
  const loads = (data as LoadRow[]).map(rowToLoad);
  if (!viewer) return loads;
  return filterLoadsForViewer(loads, viewer);
}

/**
 * List loads visible to the signed-in user. Production path: `loads` edge function
 * merges QuickManage + spreadsheet API reads with our DB overlay. Preview path:
 * localStorage seed data simulates the same API-fed experience locally.
 */
export async function listLoads(
  viewer?: AppUser | null,
): Promise<LoadRecord[]> {
  if (supabaseReady()) {
    try {
      const { loads } = await listLoadsFromEdge();
      return loads;
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        return listLoadsFromDbOverlay(viewer);
      }
      throw err;
    }
  }

  const all = localReadAll().sort(byPickupDesc);
  if (!viewer) return all;
  return filterLoadsForViewer(all, viewer);
}

export async function listActiveLoads(viewer?: AppUser | null): Promise<LoadRecord[]> {
  const loads = await listLoads(viewer);
  return loads.filter((load) => load.status !== "delivered");
}

export async function updateLoadStatus(id: string, status: LoadStatus): Promise<void> {
  if (supabaseReady()) {
    try {
      await invokeFunction("loads", { action: "updateStatus", id, status });
      return;
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 503)) throw err;
    }

    const { error } = await supabase!.from("loads").update({ status }).eq("id", id);
    if (error) throw error;
    return;
  }

  localWriteAll(
    localReadAll().map((load) =>
      load.id === id
        ? { ...load, status, updatedAt: new Date().toISOString() }
        : load,
    ),
  );
}

export async function claimLoad(
  id: string,
  carrier: Pick<AppUser, "id" | "name">,
): Promise<void> {
  const profileId = previewProfileId(carrier.id);

  if (supabaseReady()) {
    try {
      await invokeFunction("loads", { action: "claim", id });
      return;
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 503)) throw err;
    }

    const { error } = await supabase!
      .from("loads")
      .update({
        carrier_profile_id: carrier.id,
        carrier_name: carrier.name,
        status: "dispatched",
      })
      .eq("id", id);
    if (error) throw error;
    return;
  }

  localWriteAll(
    localReadAll().map((load) =>
      load.id === id && !load.carrierProfileId && load.status === "booked"
        ? {
            ...load,
            carrierProfileId: profileId,
            carrierName: carrier.name,
            status: "dispatched",
            updatedAt: new Date().toISOString(),
          }
        : load,
    ),
  );
}

export async function updateLoad(
  id: string,
  changes: {
    status?: LoadStatus;
    carrierName?: string;
    driverName?: string;
    driverProfileId?: string | null;
    carrierProfileId?: string | null;
    internalNotes?: string;
  },
  context?: Pick<LoadRecord, "reference" | "origin" | "destination" | "source" | "quickmanageId">,
): Promise<void> {
  if (supabaseReady()) {
    if (changes.internalNotes !== undefined && context?.reference) {
      try {
        await invokeFunction("loads", {
          action: "updateOverlay",
          id,
          reference: context.reference,
          origin: context.origin,
          destination: context.destination,
          source: context.source,
          quickmanageId: context.quickmanageId || undefined,
          internalNotes: changes.internalNotes,
        });
        return;
      } catch (err) {
        if (!(err instanceof ApiError && err.status === 503)) throw err;
      }
    }

    const { error } = await supabase!
      .from("loads")
      .update({
        ...(changes.status ? { status: changes.status } : {}),
        ...(changes.carrierName !== undefined ? { carrier_name: changes.carrierName || null } : {}),
        ...(changes.driverName !== undefined ? { driver_name: changes.driverName || null } : {}),
        ...(changes.driverProfileId !== undefined
          ? { driver_profile_id: changes.driverProfileId }
          : {}),
        ...(changes.carrierProfileId !== undefined
          ? { carrier_profile_id: changes.carrierProfileId }
          : {}),
        ...(changes.internalNotes !== undefined
          ? { internal_notes: changes.internalNotes }
          : {}),
      })
      .eq("id", id);
    if (error) throw error;
    return;
  }

  localWriteAll(
    localReadAll().map((load) =>
      load.id === id
        ? {
            ...load,
            status: changes.status ?? load.status,
            carrierName: changes.carrierName ?? load.carrierName,
            driverName: changes.driverName ?? load.driverName,
            driverProfileId:
              changes.driverProfileId !== undefined
                ? changes.driverProfileId
                : load.driverProfileId,
            carrierProfileId:
              changes.carrierProfileId !== undefined
                ? changes.carrierProfileId
                : load.carrierProfileId,
            internalNotes: changes.internalNotes ?? load.internalNotes,
            updatedAt: new Date().toISOString(),
          }
        : load,
    ),
  );
}
